import * as React from 'react'

import { Container } from '../../_shared/Container'
import {
  formatInteger,
  getAmountRaisedEurTotal,
  getProjectsFinancedTotal,
  type UluleStats,
} from '../../_shared/ululeStats'
import { useUluleStats } from '../../_shared/useUluleStats'
import bcorpLogo from '../../_shared/assets/bcorp.svg?url'
import styles from './Stats.module.css'

type Stat = { value: string; label: string }

const MONTHS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
] as const

// This page's own lowercase unit, per its mockup. Kept local because the other pages that show the
// member count spell it differently — Manifeste renders `5,2M` (one decimal) and Découvrir
// `5 millions` — so there is no one shared format to extract.
function formatMillions(value: number, suffix = ''): string {
  return `+${Math.floor(value / 1_000_000)}m${suffix}`
}

function makeStats(stats: UluleStats): Stat[] {
  return [
    { value: formatInteger(getProjectsFinancedTotal(stats)), label: 'projets financés' },
    { value: formatMillions(getAmountRaisedEurTotal(stats), '€'), label: 'collectés' },
    { value: formatMillions(stats.usersCount), label: 'contributeur·ices' },
  ]
}

function formatDate(value: string): string {
  const date = new Date(value)
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`
}

export function Stats({ initialStats }: { initialStats: UluleStats | null }): React.ReactElement {
  const ululeStats = useUluleStats(initialStats)

  return (
    <section className={styles.stats}>
      <Container>
        <p className={styles.stats__lede}>
          Retrouvez ici tous les éléments pour parler d'Ulule —leader du crowdfunding, fondé en 2010, B Corp depuis
          2015.
          <br />
          Ulule accompagne les créateurs et les entrepreneurs pour leur donner les meilleures chances de succès.
        </p>
        {/* Narrow on the snapshot itself, not a derived list, so `updatedAt` below is known
            non-null to the type checker. */}
        {ululeStats !== null && (
          <>
            <div className={styles.stats__grid}>
              {makeStats(ululeStats).map((stat) => (
                <div key={stat.label} className={styles.stats__item}>
                  <div className={styles.stats__num}>{stat.value}</div>
                  <div className={styles.stats__label}>{stat.label}</div>
                </div>
              ))}
              <div className={styles.stats__item}>
                <img className={styles.stats__bcorpLogo} src={bcorpLogo} alt="Certified B Corporation" />
                <div className={styles.stats__num}>105,3</div>
                <div className={styles.stats__label}>score B Corp</div>
              </div>
            </div>
            <p className={styles.stats__updatedAt}>
              Données actualisées le <time dateTime={ululeStats.updatedAt}>{formatDate(ululeStats.updatedAt)}</time>
            </p>
          </>
        )}
      </Container>
    </section>
  )
}
